import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Close } from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';
import toast from 'react-hot-toast';
import { Subscription } from '../lib/supabase';
import { CATEGORIES } from '../utils/categoryColors';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { SUPPORTED_CURRENCIES } from '../utils/currency';
import { BILLING_CYCLES, calculateNextRenewalDate } from '../utils/billingCycleUtils';
import { BillingCycle } from '../utils/billingCycleUtils';

interface EditSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription | null;
}

interface EditFormState {
  name: string;
  cost: number;
  billing_cycle: BillingCycle;
  category: string;
  payment_method: string;
  notes: string;
  currency: string;
  start_date: Date;
  next_renewal_date: Date;
  is_trial: boolean;
  trial_end_date: Date | null;
  is_shared: boolean;
  shared_with: string[];
  tags: string[];
}

const categories = CATEGORIES;

const billingCycles = BILLING_CYCLES;

const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
  open,
  onClose,
  subscription,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { getPaymentMethodNames } = usePaymentMethods(user?.id);
  const { getMainCurrency } = useUserPreferences();
  
  // Debug logging
  console.log('EditSubscriptionModal props:', { open, subscription });
  
  const [editForm, setEditForm] = useState<EditFormState>({
    name: '',
    cost: 0,
    billing_cycle: 'monthly',
    category: 'Entertainment',
    payment_method: '',
    notes: '',
    currency: 'USD', // Default currency, will be updated when preferences load
    start_date: new Date(),
    next_renewal_date: new Date(),
    is_trial: false,
    trial_end_date: null as Date | null,
    is_shared: false,
    shared_with: [] as string[],
    tags: [] as string[],
  });

  const [tagInput, setTagInput] = useState('');
  const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
  const [newPaymentMethodType, setNewPaymentMethodType] = useState('credit_card');

  // Reset new payment method name when payment method selection changes
  useEffect(() => {
    if (editForm.payment_method !== '__add_new__') {
      setNewPaymentMethodName('');
      setNewPaymentMethodType('credit_card');
    }
  }, [editForm.payment_method]);

  // Load subscription data when modal opens
  useEffect(() => {
    console.log('EditSubscriptionModal useEffect - subscription:', subscription);
    if (subscription) {
      console.log('Setting edit form with subscription data:', subscription);
      setEditForm({
        name: subscription.name,
        cost: subscription.cost,
        billing_cycle: subscription.billing_cycle as BillingCycle,
        payment_method: subscription.payment_method,
        start_date: new Date(subscription.start_date),
        next_renewal_date: new Date(subscription.next_renewal_date),
        notes: subscription.notes || '',
        category: subscription.category,
        is_shared: subscription.is_shared || false,
        shared_with: subscription.shared_with || [],
        is_trial: subscription.is_trial || false,
        trial_end_date: subscription.trial_end_date ? new Date(subscription.trial_end_date) : null,
        tags: subscription.tags || [],
        currency: subscription.currency || 'USD', // Load currency from subscription
      });
    }
  }, [subscription]);

  // Update currency when preferences are loaded
  useEffect(() => {
    if (subscription && !subscription.currency) {
      const mainCurrency = getMainCurrency();
      setEditForm(prev => ({
        ...prev,
        currency: mainCurrency
      }));
    }
  }, [subscription, getMainCurrency]);

  const updateSubscriptionMutation = useMutation(
    async (data: any) => {
      if (!subscription) throw new Error('No subscription to update');
      
      // Get the user's main currency
      const mainCurrency = getMainCurrency();
      
      const updateData: any = {
        name: data.name,
        cost: parseFloat(data.cost),
        currency: data.currency || mainCurrency, // Use selected currency or fallback to main currency
        billing_cycle: data.billing_cycle,
        payment_method: data.payment_method,
        start_date: data.start_date.toISOString().split('T')[0],
        next_renewal_date: data.next_renewal_date.toISOString().split('T')[0],
        notes: data.notes,
        category: data.category,
        is_shared: data.is_shared,
        shared_with: data.is_shared ? data.shared_with : [],
        is_trial: data.is_trial,
        trial_end_date: data.trial_end_date ? data.trial_end_date.toISOString().split('T')[0] : null,
        tags: data.tags,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('subscriptions')
        .update(updateData)
        .eq('id', subscription.id);

      if (error) throw error;
    },
    {
      onSuccess: () => {
        toast.success('Subscription updated successfully!');
        queryClient.invalidateQueries(['subscriptions', user?.id]);
        onClose();
      },
      onError: (error) => {
        toast.error('Failed to update subscription');
        console.error('Error updating subscription:', error);
      },
    }
  );

  const handleInputChange = (field: keyof EditFormState, value: any) => {
    setEditForm(prev => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Auto-calculate next renewal date when billing cycle changes
      if (field === 'billing_cycle' && value && prev.start_date) {
        const nextRenewal = calculateNextRenewalDate(prev.start_date, value);
        newData.next_renewal_date = nextRenewal;
      }

      // Auto-calculate next renewal date when start date changes
      if (field === 'start_date' && value && prev.billing_cycle) {
        const nextRenewal = calculateNextRenewalDate(value, prev.billing_cycle);
        newData.next_renewal_date = nextRenewal;
      }

      return newData;
    });
  };

  const handleTagInputKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && tagInput.trim()) {
      event.preventDefault();
      if (!editForm.tags.includes(tagInput.trim())) {
        setEditForm(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    // Handle new payment method
    let finalPaymentMethod = editForm.payment_method;
    if (editForm.payment_method === '__add_new__') {
      if (!newPaymentMethodName.trim()) {
        toast.error('Please enter a payment method name');
        return;
      }
      finalPaymentMethod = newPaymentMethodName.trim();
    }

    // Create the final form data with the correct payment method
    const finalFormData = {
      ...editForm,
      payment_method: finalPaymentMethod,
    };

    // If adding a new payment method, create it first
    if (editForm.payment_method === '__add_new__') {
      createPaymentMethodAndUpdateSubscription(finalFormData);
    } else {
      updateSubscriptionMutation.mutate(finalFormData);
    }
  };

  const createPaymentMethodAndUpdateSubscription = async (subscriptionData: any) => {
    try {
      // First create the payment method
      const { error: pmError } = await supabase
        .from('payment_methods')
        .insert({
          user_id: user?.id,
          name: newPaymentMethodName.trim(),
          type: newPaymentMethodType,
          is_active: true,
        });

      if (pmError) throw pmError;

      // Then update the subscription
      updateSubscriptionMutation.mutate(subscriptionData);
    } catch (error) {
      toast.error('Failed to create payment method');
      console.error('Error creating payment method:', error);
    }
  };

  const handleClose = () => {
    setEditForm({
      name: '',
      cost: 0,
      billing_cycle: 'monthly',
      category: 'Entertainment',
      payment_method: '',
      notes: '',
      currency: 'USD',
      start_date: new Date(),
      next_renewal_date: new Date(),
      is_trial: false,
      trial_end_date: null,
      is_shared: false,
      shared_with: [],
      tags: [],
    });
    setTagInput('');
    setNewPaymentMethodName('');
    setNewPaymentMethodType('credit_card');
    onClose();
  };

  if (!subscription) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Subscription
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subscription Name"
                value={editForm.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                placeholder="e.g., Netflix, Spotify"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={editForm.cost}
                onChange={(e) => handleInputChange('cost', e.target.value)}
                required
                placeholder="0.00"
                InputProps={{
                  startAdornment: getCurrencySymbol(editForm.currency || 'USD'),
                }}
                helperText={`Cost in ${editForm.currency || 'USD'}`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={editForm.currency || 'USD'}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  label="Currency"
                >
                  {SUPPORTED_CURRENCIES.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={editForm.billing_cycle}
                  label="Billing Cycle"
                  onChange={(e) => handleInputChange('billing_cycle', e.target.value as BillingCycle)}
                >
                  {billingCycles.map((cycle) => (
                    <MenuItem key={cycle.value} value={cycle.value}>
                      <Box>
                        <Typography variant="body1">{cycle.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cycle.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editForm.category}
                  label="Category"
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={editForm.payment_method}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                  label="Payment Method"
                >
                  <MenuItem value="">
                    <em>Select a payment method</em>
                  </MenuItem>
                  {getPaymentMethodNames().map((methodName) => (
                    <MenuItem key={methodName} value={methodName}>
                      {methodName}
                    </MenuItem>
                  ))}
                  <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                    + Add New Payment Method
                  </MenuItem>
                </Select>
              </FormControl>
              {editForm.payment_method === '__add_new__' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="New Payment Method Name"
                      value={newPaymentMethodName}
                      onChange={(e) => setNewPaymentMethodName(e.target.value)}
                      placeholder="e.g., My MasterCard, Business PayPal"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={newPaymentMethodType}
                        onChange={(e) => setNewPaymentMethodType(e.target.value)}
                        label="Type"
                      >
                        <MenuItem value="credit_card">Credit Card</MenuItem>
                        <MenuItem value="debit_card">Debit Card</MenuItem>
                        <MenuItem value="paypal">PayPal</MenuItem>
                        <MenuItem value="e_wallet">E-Wallet</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={editForm.start_date}
                  onChange={(newValue) => handleInputChange('start_date', newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Next Renewal Date"
                  value={editForm.next_renewal_date}
                  onChange={(newValue) => handleInputChange('next_renewal_date', newValue)}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      helperText: "Auto-calculated based on billing cycle, but you can adjust manually"
                    } 
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={editForm.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                multiline
                rows={3}
                placeholder="Additional notes about this subscription..."
              />
            </Grid>

            {/* Trial Information */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.is_trial}
                    onChange={(e) => handleInputChange('is_trial', e.target.checked)}
                  />
                }
                label="This is a free trial"
              />
            </Grid>

            {editForm.is_trial && (
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Trial End Date"
                    value={editForm.trial_end_date}
                    onChange={(newValue) => handleInputChange('trial_end_date', newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
            )}

            {/* Shared Subscription */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.is_shared}
                    onChange={(e) => handleInputChange('is_shared', e.target.checked)}
                  />
                }
                label="This is a shared subscription"
              />
            </Grid>

            {editForm.is_shared && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shared With"
                  value={editForm.shared_with.join(', ')}
                  onChange={(e) => handleInputChange('shared_with', e.target.value.split(',').map(s => s.trim()))}
                  placeholder="Enter email addresses separated by commas"
                  helperText="Enter email addresses of people you share this subscription with"
                />
              </Grid>
            )}

            {/* Tags */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                placeholder="Press Enter to add tags"
                helperText="Press Enter to add tags"
              />
              {editForm.tags.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {editForm.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={updateSubscriptionMutation.isLoading}
          >
            {updateSubscriptionMutation.isLoading ? 'Updating...' : 'Update Subscription'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSubscriptionModal; 